import { Avatar, Box, Card, Chip, IconButton, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import {
  BooleanInput,
  Datagrid,
  Filter,
  FunctionField,
  List,
  Pagination,
  SelectInput,
  TextInput,
  TopToolbar,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import { fShortenNumber } from 'src/utils/format-number';
import { apiGet, displayLabel } from '../perps-v1-shared/api';
import { PERPS_V1_SYMBOLS_PERMISSION } from '../perps-v1-shared/constants';
import { errorMessage } from '../perps-v1-shared/errorMessage';
import { MarketSector, MarketView } from '../perps-v1-shared/types';
import ImportDialog from './components/ImportDialog';
import { SortableDatagridHeader } from './components/SortableDatagridHeader';
import SymbolEditorDialog from './components/SymbolEditorDialog';
import { SymbolRow, onDisplayMergeError, symbolsApi } from './dataProvider';
import './init';

// GET /perps-v1/views/dexes 的一行：dex 是过滤时使用的原始值（空字符串代表 native），
// id 仅在 dex === '' 时固定为 'native'（因为 '' 不能用作 React key/SelectInput 的 choice id），
// 其余行 id 与 dex 相同；dataProvider 里的 'native' -> '' 换算与此约定保持一致。
interface DexOption {
  id: string;
  dex: string;
}

function SymbolLogo({ logoUrl, label }: { logoUrl?: string | null; label: string }) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(logoUrl) && !failed;
  return (
    <Avatar
      src={showImage ? (logoUrl as string) : undefined}
      imgProps={{ onError: () => setFailed(true) }}
      sx={{ width: 28, height: 28, fontSize: 13 }}
    >
      {label.charAt(0).toUpperCase()}
    </Avatar>
  );
}

type SymbolsFilterProps = { sectors: MarketSector[]; dexes: DexOption[] } & Record<string, any>;

function SymbolsFilter({ sectors, dexes, ...rest }: SymbolsFilterProps) {
  const translate = useTranslate();
  return (
    <Filter {...rest}>
      <TextInput
        source="search"
        label={translate('module.perpsV1Symbols.filters.search')}
        size="small"
        alwaysOn
      />
      {/* emptyText labels the row inside the dropdown, but MUI's Select only renders a matching
          MenuItem's label in the CLOSED field when `displayEmpty` is set — without it, an empty
          string value (`isFilled('') === false`) is treated as "nothing to show" and the closed
          field goes blank, even though the option itself is correctly labelled. `''` stays the
          real value (still omitted from the wire in dataProvider.ts); only the rendering changes.
          That same `isFilled('') === false` check also drives whether MUI floats the `InputLabel`:
          with an empty value it stays in its resting position while `displayEmpty` paints text
          underneath it, so the label and the value overlap. `InputLabelProps={{ shrink: true }}`
          pins the label to its floated position regardless of value (checked with both an empty and
          a real selection). For the `outlined` variant MUI's own TextField wires this same flag to
          the notched-outline: `notched` defaults to `InputLabelProps.shrink` (see
          @mui/material/TextField/TextField.js), so the outline opens to match the floated label with
          no separate prop needed. `margin="none"` matches the TextInput above it — SelectInput
          otherwise defaults to `margin="dense"`, which adds 8px/4px of outer FormControl margin the
          TextInput doesn't have and throws the row off its shared baseline. */}
      <SelectInput
        source="dex"
        label={translate('module.perpsV1Symbols.filters.dex')}
        emptyText="module.perpsV1Symbols.filters.dexAll"
        choices={dexes.map((d) => ({
          id: d.id,
          name: d.dex ? d.dex : translate('module.perpsV1Symbols.dexNative'),
        }))}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        size="small"
        margin="none"
        alwaysOn
      />
      <SelectInput
        source="sector"
        label={translate('module.perpsV1Symbols.filters.sector')}
        emptyText="module.perpsV1Symbols.filters.sectorAll"
        choices={sectors.map((s) => ({ id: s.code, name: displayLabel(s.name, s.code) }))}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        size="small"
        margin="none"
        alwaysOn
      />
      <BooleanInput
        source="untaggedOnly"
        label={translate('module.perpsV1Symbols.filters.untaggedOnly')}
        alwaysOn
      />
      <BooleanInput
        source="includeDelisted"
        label={translate('module.perpsV1Symbols.filters.includeDelisted')}
        alwaysOn
      />
      {/* Grouped with untaggedOnly/includeDelisted (not the three filters above): same toggle
          shape, and the divider that sets the group apart is keyed off `[data-source="untaggedOnly"]`
          only, so adding a third toggle after includeDelisted keeps the existing grouping intact
          without touching that CSS. */}
      <BooleanInput
        source="missingLogo"
        label={translate('module.perpsV1Symbols.filters.missingLogo')}
        alwaysOn
      />
    </Filter>
  );
}

function ListActions({
  onImport,
  onSyncIcons,
  syncingIcons,
}: {
  onImport: () => void;
  onSyncIcons: () => void;
  syncingIcons: boolean;
}) {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
        <LoadingButton
          variant="outlined"
          onClick={onSyncIcons}
          loading={syncingIcons}
          sx={{ mr: 1 }}
        >
          {translate('module.perpsV1Symbols.syncIcons')}
        </LoadingButton>
      </PermissionButton>
      <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
        <LoadingButton variant="contained" onClick={onImport}>
          {translate('module.perpsV1Symbols.import')}
        </LoadingButton>
      </PermissionButton>
    </TopToolbar>
  );
}

const View = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const [sectors, setSectors] = useState<MarketSector[]>([]);
  const [dexes, setDexes] = useState<DexOption[]>([]);
  const [views, setViews] = useState<MarketView[]>([]);
  const [editingRow, setEditingRow] = useState<SymbolRow | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [syncingIcons, setSyncingIcons] = useState(false);

  useEffect(() => {
    let active = true;
    apiGet<{ data: MarketSector[]; total: number }>('/perps-v1/sectors')
      .then((json) => {
        if (active) setSectors(json?.data ?? []);
      })
      .catch(() => {
        if (active) setSectors([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    apiGet<{ data: DexOption[]; total: number }>('/perps-v1/views/dexes')
      .then((json) => {
        if (active) setDexes(json?.data ?? []);
      })
      .catch(() => {
        if (active) setDexes([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // 拉取全量 view 树（含 disabled），用于弹窗内计算叶子节点 + 祖先路径
  useEffect(() => {
    let active = true;
    apiGet<{ data: MarketView[]; total: number }>('/perps-v1/views')
      .then((json) => {
        if (active) setViews(json?.data ?? []);
      })
      .catch(() => {
        if (active) setViews([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // getList 合并展示资料失败时不阻断整页渲染，这里只弹一次错误提示
  useEffect(
    () =>
      onDisplayMergeError((error: any) => {
        notify(errorMessage(error, translate('module.perpsV1Symbols.loadError')), {
          type: 'error',
        });
      }),
    [notify, translate]
  );

  const sectorLabel = useMemo(() => {
    const map = new Map(sectors.map((s) => [s.code, displayLabel(s.name, s.code)]));
    return (code: string) => map.get(code) ?? code;
  }, [sectors]);

  const openEdit = (row: SymbolRow) => {
    setEditingRow(row);
    setEditorOpen(true);
  };

  // 弹窗内保存（tags/display）后原地更新 editingRow，避免弹窗仍打开时展示过期数据；
  // 同时仍调用 refresh() 让底层网格拿到最新分页数据。
  const handleSaved = (updatedRow?: Partial<SymbolRow>) => {
    if (updatedRow) {
      setEditingRow((prev) => (prev ? { ...prev, ...updatedRow } : prev));
    }
    refresh();
  };

  // refresh() re-invokes getList, which re-runs both the /symbols page fetch AND the
  // /symbols/display merge (see dataProvider.ts — they're a single Promise.all), so newly filled
  // logos show up without a separate "reload display meta" step.
  const handleSyncIcons = async () => {
    setSyncingIcons(true);
    try {
      const result = await symbolsApi.syncIcons();
      notify(
        translate('module.perpsV1Symbols.syncIconsSuccess', {
          filled: result?.filled ?? 0,
          skippedCurated: result?.skippedCurated ?? 0,
          unavailable: result?.unavailable ?? 0,
        }),
        { type: 'success' }
      );
      refresh();
    } catch (error: any) {
      notify(errorMessage(error, translate('module.perpsV1Symbols.syncIconsError')), {
        type: 'error',
      });
    } finally {
      setSyncingIcons(false);
    }
  };

  return (
    <PermissionGuard resource={PERPS_V1_SYMBOLS_PERMISSION} action="read">
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={translate('module.perpsV1Symbols.title')}
          links={[{ name: translate('resource.perpsV1') }, { name: translate('resource.perpsV1Symbols') }]}
          sx={{ mb: 1 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('module.perpsV1Symbols.subtitle')}
        </Typography>

        <Card
          sx={{
            // react-admin's <Filter> bottom-aligns each input by default (TextInput/SelectInput
            // render as ~56px filled fields, BooleanInput renders a short Switch+label), which reads
            // as five mismatched baselines. Center everything on one row instead.
            // NB: target `.RaFilter-form` / `.filter-field`, not MUI's auto `Name-root` classes —
            // those only carry their readable "-RaFilterForm-root" suffix in dev (see
            // @mui/system/createStyled.js gating it on NODE_ENV !== 'production'); in a real
            // production build the emotion class is just an opaque hash, so a selector built on that
            // suffix would silently stop matching anything. `RaFilter-form` and `filter-field` are
            // plain string constants react-admin assigns unconditionally, so they survive the build.
            // columnGap (theme.spacing(2) = 16px) gives every pair of adjacent filter fields a real
            // gap; the untaggedOnly rule below stacks its own ml/pl on top of this, so it still reads
            // as a visibly wider gap than the ordinary one between the other fields.
            '& .RaFilter-form': { alignItems: 'center', rowGap: 1, columnGap: 2 },
            '& .filter-field': { alignItems: 'center' },
            // Group the three view-option toggles visually apart from the three filters with a gap
            // and a subtle divider, without touching what any of the six alwaysOn inputs do.
            // ml/pl (24px each) stack on top of the RaFilter-form columnGap (16px) above, so the
            // total gap before this divider (~40px+) stays clearly wider than the ordinary 16px gap.
            '& .filter-field[data-source="untaggedOnly"]': {
              ml: 3,
              pl: 3,
              borderLeft: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <List
            filters={<SymbolsFilter sectors={sectors} dexes={dexes} />}
            actions={
              <ListActions
                onImport={() => setImportOpen(true)}
                onSyncIcons={handleSyncIcons}
                syncingIcons={syncingIcons}
              />
            }
            exporter={false}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[25, 50, 100, 150]} />}
            empty={false}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid bulkActionButtons={false} rowClick={false} header={<SortableDatagridHeader />}>
                <FunctionField
                  label={translate('module.perpsV1Symbols.columns.logo')}
                  sortable={false}
                  render={(record: SymbolRow) => (
                    <SymbolLogo logoUrl={record.logoUrl} label={record.displaySymbol || record.symbol} />
                  )}
                />
                <FunctionField
                  source="symbol"
                  label={translate('module.perpsV1Symbols.columns.symbol')}
                  render={(record: SymbolRow) => (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2" fontFamily="monospace">
                        {record.displaySymbol || record.symbol}
                      </Typography>
                      {record.isDelisted ? (
                        <Chip
                          size="small"
                          color="default"
                          label={translate('module.perpsV1Symbols.delistedChip')}
                        />
                      ) : null}
                    </Stack>
                  )}
                />
                <FunctionField
                  label={translate('module.perpsV1Symbols.columns.name')}
                  sortable={false}
                  render={(record: SymbolRow) =>
                    record.name && displayLabel(record.name, '') ? (
                      <Box>
                        <Typography variant="body2">{displayLabel(record.name, '')}</Typography>
                        {record.name.zh ? (
                          <Typography variant="caption" color="text.secondary">
                            {record.name.zh}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {record.displaySymbol || record.symbol}
                      </Typography>
                    )
                  }
                />
                <FunctionField
                  label={translate('module.perpsV1Symbols.columns.dex')}
                  sortable={false}
                  render={(record: SymbolRow) => (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={record.dex ? record.dex : translate('module.perpsV1Symbols.dexNative')}
                    />
                  )}
                />
                <FunctionField
                  label={translate('module.perpsV1Symbols.columns.sectors')}
                  sortable={false}
                  render={(record: SymbolRow) => (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(record.sectors ?? []).map((s) => (
                        <Chip key={s.code} size="small" variant="outlined" label={sectorLabel(s.code)} />
                      ))}
                    </Stack>
                  )}
                />
                <FunctionField
                  label={translate('module.perpsV1Symbols.columns.tags')}
                  sortable={false}
                  render={(record: SymbolRow) => (
                    <Chip
                      size="small"
                      color={record.tagsSource === 'AUTO' ? 'info' : 'default'}
                      label={
                        record.tagsSource === 'AUTO'
                          ? translate('module.perpsV1Symbols.tagsAuto')
                          : `${translate('module.perpsV1Symbols.tagsManual')}${
                              record.tagsLocked ? ' \u{1F512}' : ''
                            }`
                      }
                    />
                  )}
                />
                <FunctionField
                  source="volume24h"
                  label={translate('module.perpsV1Symbols.columns.volume')}
                  render={(record: SymbolRow) => (
                    <Typography variant="body2">{fShortenNumber(record.volume24h)}</Typography>
                  )}
                />
                <FunctionField
                  label={translate('module.perpsV1Shared.actions')}
                  sortable={false}
                  render={(record: SymbolRow) => (
                    <PermissionButton resource={PERPS_V1_SYMBOLS_PERMISSION} action="update">
                      <IconButton size="small" onClick={() => openEdit(record)}>
                        <Iconify icon="eva:edit-2-outline" />
                      </IconButton>
                    </PermissionButton>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <SymbolEditorDialog
          open={editorOpen}
          row={editingRow}
          sectors={sectors}
          views={views}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
        />
        <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={handleSaved} />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
